import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ConversationProvider } from "@elevenlabs/react";

import "./App.css";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { VoiceProvider } from "./voice/VoiceProvider";

import AppShell from "./components/AppShell";
import Splash from "./components/Splash";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AuthCallback from "./pages/AuthCallback";
import Terms from "./pages/Terms";
import Dashboard from "./pages/Dashboard";
import CalendarPage from "./pages/Calendar";
import Murshid from "./pages/Murshid";
import Geolocation from "./pages/Geolocation";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import ComingSoon from "./pages/ComingSoon";
import WebhookReport from "./pages/WebhookReport";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!user.terms_accepted && location.pathname !== "/terms") {
    return <Navigate to="/terms" replace />;
  }
  return <AppShell>{children}</AppShell>;
}

function AppRouter() {
  // Synchronous fragment check before any other routing
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/terms" element={<TermsRoute />} />

      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
      <Route path="/murshid" element={<Protected><Murshid /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/messaging/*" element={<Protected><ComingSoon title="Messaging" blurb="Real-time chats, groups, broadcast lists and AI Dnya Assistant thread — coming in the next phase Insha'Allah." /></Protected>} />
      <Route path="/geolocation/*" element={<Protected><Geolocation /></Protected>} />
      <Route path="/hajj/*" element={<Protected><ComingSoon title="Hajj & Umrah" blurb="Plan your pilgrimage with an AI itinerary, packing checklist and rituals guide — coming next." /></Protected>} />
      <Route path="/settings/webhooks" element={<Protected><WebhookReport /></Protected>} />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function TermsRoute() {
  const { loading } = useAuth();
  if (loading) return <Splash />;
  return <Terms />;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <ConversationProvider>
            <VoiceProvider>
              <Toaster position="top-center" theme="dark" toastOptions={{ style: { background: "#0f3d0f", border: "1px solid #CEF0CE", color: "#ffffff" } }} />
              <AppRouter />
            </VoiceProvider>
          </ConversationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
